import React from "react";

// Seller-only "Filter combinations" grid -- add + edit + remove for
// filterProduct entries, in one place. NOT shared with Provider's own
// combinations grid (ProviderFiltersEditor.jsx): Provider's `variants` are an
// ephemeral array saved wholesale in one PATCH, with no per-row id/diffing/
// duplicate-check/price -- Seller's rows carry a real filterProduct `_id`
// (or a client-only placeholder for a not-yet-saved row), get diffed against
// the last-loaded snapshot, and are saved individually through the existing
// createFilterProduct/updateFilterProduct/deleteFilterProduct endpoints (see
// editProduct.jsx's saveFilterCombinations()). Only the value-selection piece
// above this grid (FilterValuePicker) is genuinely identical and shared.
//
// Props:
//   activeDefs   -- [{ filterId, key, name, values: [{value,label}] }], only
//                    filters with at least one checked value in "Filters
//                    from your linked categories" (mirrors Provider's
//                    activeDefs pattern).
//   rows         -- [{ id, filter: {[key]:value}, quantity, priceFil }]
//   onFieldChange(rowId, defKey, value)
//   onQuantityChange(rowId, value)
//   onPriceChange(rowId, value)
//   onRemoveRow(rowId)
//   onAddRow()
//   duplicateRowIds   -- Set of row ids that exactly match another row
//   incompleteRowIds  -- Set of row ids missing a required value/quantity
//   labels       -- { title, quantity, price, addCombination, removeCombination,
//                     duplicateError, incompleteError, noActiveFilters, quantityLockedHint }
//   quantityLocked -- provider_internal products: quantity is Provider-controlled
//                      availability, synced by Resynchronize -- the field is
//                      shown but read-only (server also ignores a direct write,
//                      see updateFilterProduct).
const SellerFilterCombinationsEditor = ({
  activeDefs,
  rows,
  onFieldChange,
  onQuantityChange,
  onPriceChange,
  onRemoveRow,
  onAddRow,
  duplicateRowIds,
  incompleteRowIds,
  labels,
  disabled = false,
  quantityLocked = false,
}) => {
  return (
    <div className="seller-filter-combinations">
      {activeDefs.length === 0 && rows.length === 0 && (
        <p className="text-muted">{labels.noActiveFilters}</p>
      )}
      {rows.map((row) => {
        const isDuplicate = duplicateRowIds.has(row.id);
        const isIncomplete = incompleteRowIds.has(row.id);
        return (
          <div
            key={row.id}
            className={`provider-filters__variant seller-filter-combo-row${isDuplicate || isIncomplete ? " seller-filter-combo-row--error" : ""}`}
          >
            {activeDefs.map((def) => (
              <label key={def.filterId} className="seller-filter-combo-field">
                <span className="seller-filter-combo-field-label">{def.name}</span>
                <select
                  className="form-control"
                  disabled={disabled}
                  aria-invalid={isIncomplete || isDuplicate}
                  value={row.filter?.[def.key] || ""}
                  onChange={(e) => onFieldChange(row.id, def.key, e.target.value)}
                >
                  <option value="">{labels.selectPlaceholder}</option>
                  {def.values.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
              </label>
            ))}
            <label className="seller-filter-combo-field">
              <span className="seller-filter-combo-field-label">{labels.quantity}</span>
              <input
                type="number"
                min="0"
                step="1"
                disabled={disabled || quantityLocked}
                title={quantityLocked ? labels.quantityLockedHint : undefined}
                aria-invalid={isIncomplete}
                className="form-control"
                value={row.quantity}
                onChange={(e) => onQuantityChange(row.id, e.target.value)}
              />
              {quantityLocked && <span className="seller-filter-combo-field-hint">{labels.quantityLockedHint}</span>}
            </label>
            <label className="seller-filter-combo-field">
              <span className="seller-filter-combo-field-label">{labels.price}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                disabled={disabled}
                aria-invalid={isIncomplete}
                className="form-control"
                value={row.priceFil ?? ""}
                onChange={(e) => onPriceChange(row.id, e.target.value)}
              />
            </label>
            <button
              type="button"
              disabled={disabled}
              className="btn btn-sm btn-outline-danger seller-filter-combo-remove"
              onClick={() => onRemoveRow(row.id)}
            >
              {labels.removeCombination}
            </button>
            {isDuplicate && <p className="seller-filter-combo-error">{labels.duplicateError}</p>}
            {!isDuplicate && isIncomplete && <p className="seller-filter-combo-error">{labels.incompleteError}</p>}
          </div>
        );
      })}
      <button type="button" className="btn btn-outline-primary btn-sm" disabled={disabled || (activeDefs.length === 0 && rows.length > 0)} onClick={onAddRow}>
        {labels.addCombination}
      </button>
    </div>
  );
};

export default SellerFilterCombinationsEditor;
